<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { $api } from '@/services';
import dayjs from 'dayjs';
import Loading from '@/components/common/Loading';
import { useStore } from 'vuex';
import UploadDocument from './UploadDocument.vue';
import OthersInfo from './OthersInfo.vue';

onMounted(() => {
	getLucaHubData();
});

const data = ref({});
const orgList = ref([]);
const parentContactInformation = ref([]);
const date = ref({
	month: new Date().getMonth(),
	year: new Date().getFullYear(),
});
const isLoading = ref(true);
const mainService = ref({
	diagnostic_evaluation: 'Đánh giá chẩn đoán',
	intervention_facility: 'Cơ sở can thiệp	',
	integrated_intervention_diagnostic_facility:
		'Cơ sở can thiệp & Đánh giá chẩn đoán',
});
const fileUpload = ref(null);
const key = ref(null);
const store = useStore();
const type = ref(0);
const sideOverShow = ref(false);
const alertShow = ref(false);
const showToast = ref(false);
const showOthersInfo = ref(false);

const getLucaHubData = async () => {
	try {
		const res = await $api.org.lucaHub(datePayload.value);
		if (res) {
			data.value = res.data;
			orgList.value = res.data.org_list;
			parentContactInformation.value =
				res.data.parent_contact_informations;
			if (data.value.data.shor_form_url) {
				alertShow.value = true;
			}
		}
		isLoading.value = false;
	} catch (e) {
		console.log(e);
	}
};

const registerLucaHub = async () => {
	try {
		const res = await $api.org.registerLucaHub();
		if (res) {
			const a = document.createElement('a');
			a.href = res.data.url;
			a.target = '_blank';
			a.rel = 'noopener noreferrer';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		}
	} catch (e) {
		console.log(e);
	}
};

watch(
	date,
	() => {
		getLucaHubData();
	},
	{ deep: true }
);
const havePendingDocument = computed(() => {
	return store.state.profile.is_pending_lucahub_document || showToast.value;
});
const dateTitle = computed(() => {
	return dayjs(new Date(date.value.year, date.value.month, 1)).format(
		'MM/YYYY'
	);
});
const datePayload = computed(() => {
	return dayjs(new Date(date.value.year, date.value.month, 10)).format(
		'YYYY-MM-DD'
	);
});
const prevDay = () => {
	const da = dayjs(new Date(date.value.year, date.value.month, 1))
		.subtract(1, 'month')
		.toDate();
	date.value = {
		month: da.getMonth(),
		year: da.getFullYear(),
	};
};
const nextDay = () => {
	const da = dayjs(new Date(date.value.year, date.value.month, 1))
		.add(1, 'month')
		.toDate();
	date.value = {
		month: da.getMonth(),
		year: da.getFullYear(),
	};
};
const isImage = (icon) => {
	const ext = ['.jpg', '.jpeg', '.png', '.svg'];
	return ext.some((el) => icon.toLowerCase().endsWith(el));
};
const openModelUpload = (val) => {
	type.value = val;
	const target = document.getElementById('upload-images-notification');
	target ? target.click() : '';
};
const closeUpload = () => {
	const target = document.getElementById('upload-images-notification');
	target ? (target.value = null) : '';
};
const uploadChange = (event) => {
	let fileUploads = Array.from(event?.target?.files || []);
	if (!fileUploads?.length || !isImage(fileUploads[0].name)) {
		return;
	}
	fileUpload.value = fileUploads;
	if (type.value === 0) uploadBackgroundLucaHub();
	else uploadLogoLucaHub();
	closeUpload();
};
const uploadBackgroundLucaHub = async () => {
	try {
		if (fileUpload.value) {
			await getSignatures();
		}
		const res = await $api.org.uploadBackgroundLucaHub(
			data.value?.data?.id,
			{
				background: {
					asset_key: key.value,
					mime_type: fileUpload.value[0].type,
					name: fileUpload.value[0].name,
				},
			}
		);
		if (res) {
			getLucaHubData();
		}
	} catch (e) {
		console.log(e);
	}
};
const uploadLogoLucaHub = async () => {
	try {
		if (fileUpload.value) {
			await getSignatures();
		}
		const res = await $api.org.uploadLogoLucaHub(data.value?.data?.id, {
			logo: {
				asset_key: key.value,
				mime_type: fileUpload.value[0].type,
				name: fileUpload.value[0].name,
			},
		});
		if (res) {
			getLucaHubData();
		}
	} catch (e) {
		console.log(e);
	}
};

const getSignatures = async () => {
	const fileList = fileUpload.value;
	const {
		data: { data },
	} = await $api.file.getSignatures({
		mime_types: fileList.map((f) => f.type),
		prefix: 'lucahub',
	});
	if (data && data.length) {
		for (let i = 0; i < data.length; i++) {
			let formData = new FormData();
			formData.append('AWSAccessKeyId', data[0]['AWSAccessKeyId'] || '');
			formData.append('key', data[i].key || '');
			formData.append('acl', data[i].acl || '');
			formData.append('Content-Type', data[i]['Content-Type'] || '');
			formData.append('x-amz-signature', data[i].signature || '');
			formData.append('Signature', data[i].signature || '');
			formData.append('Policy', data[i].policy || '');
			formData.append(
				'success_action_status',
				data[i].success_action_status || ''
			);
			formData.append('file', fileList[i]);
			await uploadToS3(data[i].action, formData, fileList[i]);
		}
	}
};

const uploadToS3 = async (url, payload, file) => {
	try {
		const { data } = await $api.file.uploadToS3(url, payload);
		const keyData = data.toString().match('<Key>(.*)</Key>');
		key.value = keyData && keyData.length ? keyData[1] : '';
	} catch (e) {
		store.dispatch('setAlertMessage', {
			message:
				e?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};
const reloadPage = () => {
	sideOverShow.value = false;
	showToast.value = true;
};
const refreshData = () => {
	getLucaHubData();
	showOthersInfo.value = false;
};
</script>
<template>
	<input
		id="upload-images-notification"
		accept="image/png, image/jpeg, image/jpg"
		hidden
		type="file"
		@change="uploadChange"
	/>
	<div :class="sideOverShow ? 'side-over--active' : ''" v-if="sideOverShow">
		<div
			class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
			@click="sideOverShow = false"
		></div>
		<upload-document
			:day="data.data?.established_date"
			:id="data.data?.id"
			:open="sideOverShow"
			@update="reloadPage"
			@close="sideOverShow = false"
		/>
	</div>
	<div class="px-12 py-10 md-down:px-4">
		<div v-if="showOthersInfo">
			<OthersInfo
				@close="showOthersInfo = false"
				:othersInfo="data.data.other_info"
				:id="data.data.id"
				@refresh="refreshData"
				:mainService="data?.data?.main_service"
			/>
		</div>
		<div v-else>
			<div>
				<img
					style="height: 44px"
					src="@/assets/images/illustrations/lucahub.png"
					alt="luca-hub"
				/>
			</div>
			<div v-if="isLoading" class="flex items-center justify-center h-96">
				<Loading />
			</div>
			<div v-else>
				<div class="my-10" v-if="havePendingDocument">
					<div
						class="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded-md"
					>
						<div class="relative">
							<div class="flex items-center gap-4">
								<div>
									<img
										src="@/assets/images/logo/logo.svg"
										alt="luca-logo"
										height="24"
										width="35"
									/>
								</div>
								<div class="pt-1 text-neutral-600 text-sm">
									Hồ sơ đang được Luca Hub xem xét và phê
									duyệt. Bạn sẽ nhận được email thông báo khi
									hoàn tất.
								</div>
							</div>
							<div
								class="top-1 right-2 absolute cursor-pointer"
								@click="
									() => {
										havePendingDocument = false;
										showToast = false;
									}
								"
							>
								<svg
									width="12"
									height="12"
									viewBox="0 0 12 12"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L6 4.58579L10.2929 0.292893C10.6834 -0.0976311 11.3166 -0.0976311 11.7071 0.292893C12.0976 0.683417 12.0976 1.31658 11.7071 1.70711L7.41421 6L11.7071 10.2929C12.0976 10.6834 12.0976 11.3166 11.7071 11.7071C11.3166 12.0976 10.6834 12.0976 10.2929 11.7071L6 7.41421L1.70711 11.7071C1.31658 12.0976 0.683417 12.0976 0.292893 11.7071C-0.0976311 11.3166 -0.0976311 10.6834 0.292893 10.2929L4.58579 6L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z"
										fill="#E5A42B"
									/>
								</svg>
							</div>
						</div>
					</div>
				</div>
				<div
					v-if="
						data.is_have_lucahub_profile &&
						!data.is_pending_accepted_profile
					"
					class="mt-12"
				>
					<div
						class="flex items-stretch gap-4 mb-10 xl-down:flex-col"
					>
						<div
							class="rounded-md overflow-hidden bg-white shadow-sm block-1"
						>
							<div class="relative">
								<div
									class="w-full overflow-hidden relative cover-img"
								>
									<div
										v-if="data?.data?.background_url"
										class="w-full h-full"
									>
										<img
											class="w-full h-full object-cover"
											:src="data?.data?.background_url"
											alt="center_img"
										/>
									</div>
									<img
										v-else
										class="w-full h-full object-cover"
										src="@/assets/images/illustrations/default_background.png"
										alt="center_img"
									/>
									<div
										class="flex items-center gap-2 change-bg"
									>
										<div
											class="flex items-center gap-2 cursor-pointer py-2 px-3 rounded shadow-sm bg-white h-8"
											@click="showOthersInfo = true"
										>
											<img
												class="w-4 h-4"
												src="@/assets/images/icons/pen_fill.png"
												alt=""
											/>
											<div
												class="text-neutral-600 text-xs font-semibold whitespace-nowrap"
											>
												Quản lý nội dung
											</div>
										</div>
										<div
											class="flex items-center gap-2 cursor-pointer py-2 px-3 rounded shadow-sm bg-white h-8"
											@click="openModelUpload(0)"
										>
											<img
												src="@/assets/images/icons/camera.png"
												alt=""
												class="w-4 h-4"
											/>
											<div
												class="text-neutral-600 text-xs font-semibold whitespace-nowrap"
											>
												Chỉnh sửa ảnh bìa
											</div>
										</div>
									</div>
								</div>
								<div
									class="absolute logo flex items-center justify-center rounded-full"
									:style="
										data?.data?.logo_url
											? 'background: #fff'
											: 'background: #CBD5E1'
									"
								>
									<div
										class="logo-media absolute inset-0 rounded-full overflow-hidden"
									>
										<img
											v-if="data?.data?.logo_url"
											class="block w-full h-full object-cover"
											:src="data?.data?.logo_url"
											alt="logo"
										/>
									</div>
									<div
										class="change-logo w-7 h-7 flex items-center justify-center absolute cursor-pointer"
										@click.stop="openModelUpload(1)"
									>
										<img
											src="@/assets/images/icons/camera.png"
											alt="change-logo"
											class="w-4 h-4"
										/>
									</div>
								</div>
							</div>
							<div class="p-4 profile-info">
								<div
									class="flex items-center justify-between w-full gap-2 md-down:flex-col md-down:items-center"
								>
									<div class="md-down:text-center">
										<div
											class="flex items-center gap-2 md-down:justify-center"
										>
											<div
												class="text-neutral-600 font-semibold text-lg"
											>
												{{ data?.data?.name }}
											</div>
											<img
												v-if="
													data?.data
														?.is_basic_info_verified
												"
												src="@/assets/images/icons/verified.png"
												alt="verified"
											/>
										</div>
										<div
											class="mt-1 text-sm text-neutral-500"
										>
											{{ data?.data?.address }}
										</div>
									</div>
									<a
										:href="data?.data?.lucahub_url"
										target="_blank"
										type="button"
										class="button-p text-center w-max md-down:mt-3"
									>
										Xem hồ sơ trên Luca Hub
									</a>
								</div>
							</div>
							<div
								class="line bg-neutral-200 block w-full"
								style="height: 1px"
							></div>
							<div
								class="flex items-center justify-between p-4 flex-wrap md-down:flex-col md-down:items-stretch md-down:gap-4 highlight-list"
							>
								<div
									class="flex items-center gap-3 highlight-item"
									v-if="data?.data?.main_service"
								>
									<div class="highlight-icon">
										<img
											src="@/assets/images/icons/badge.png"
											alt=""
										/>
									</div>
									<div class="highlight-text">
										<div class="text-neutral-700 text-xs">
											Dịch vụ chính
										</div>
										<div
											class="text-neutral-700 text-sm font-semibold"
										>
											{{
												mainService[
													data?.data?.main_service
												]
											}}
										</div>
									</div>
								</div>
								<div
									class="flex items-center gap-3 highlight-item"
									v-if="
										data?.data?.year_of_experience
											?.years_since_established > 1
									"
								>
									<div class="highlight-icon">
										<img
											v-if="
												data?.data?.year_of_experience
													?.url
											"
											:src="
												data?.data?.year_of_experience
													?.url
											"
											alt=""
										/>
									</div>
									<div class="highlight-text">
										<div
											class="text-lime-600 text-sm font-semibold"
										>
											{{
												data?.data?.year_of_experience
													?.years_since_established ||
												0
											}}
											năm kinh nghiệm
										</div>
										<div class="text-neutral-700 text-sm">
											{{
												data?.data?.year_of_experience
													?.text
											}}
										</div>
									</div>
								</div>
								<div
									class="flex items-center gap-3 highlight-item"
									v-if="data?.data?.is_expert_certification"
								>
									<div class="highlight-icon">
										<img
											src="@/assets/images/icons/chuyen_mon.png"
											alt=""
										/>
									</div>
									<div class="highlight-text">
										<div
											class="text-orange-600 text-sm font-semibold"
										>
											Chứng nhận chuyên môn cao
										</div>
										<div class="text-neutral-700 text-sm">
											Khẳng định chuyên môn vượt trội
										</div>
									</div>
								</div>
							</div>
						</div>
						<div
							class="rounded-md overflow-hidden bg-white shadow-sm p-4 px-auto profile-progress xl-down:w-full flex flex-col items-center justify-between md-down:border md-down:border-neutral-200"
						>
							<div>
								<div
									class="text-neutral-600 font-semibold text-lg text-center"
								>
									Hoàn thành hồ sơ của bạn
								</div>
								<div
									class="text-neutral-500 text-sm text-center"
								>
									Hồ sơ của bạn đã hoàn thành
									{{ data.data?.complete_info_percent }}%, cập
									nhật thêm thông tin để thu hút nhiều phụ
									huynh hơn
								</div>
							</div>
							<div
								class="flex items-center justify-center gap-10 xl-down:mt-4 md-down:mt-6"
							>
								<div class="container">
									<div
										class="progress-circle"
										:style="{
											background: `conic-gradient(orange 0% ${data.data?.complete_info_percent}%, #f1f4f8 ${data.data?.complete_info_percent}% 100%)`,
										}"
									>
										<div class="inner-circle">
											{{
												data.data
													?.complete_info_percent ||
												0
											}}%
										</div>
									</div>
								</div>

								<div class="side-checklist gap-3">
									<div class="flex items-center gap-2">
										<img
											v-if="data.data?.logo_url"
											src="@/assets/images/icons/green-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<img
											v-else
											src="@/assets/images/icons/gray-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<div class="font-semibold text-sm">
											Logo
										</div>
									</div>
									<div class="flex items-center gap-2">
										<img
											v-if="data.data?.background_url"
											src="@/assets/images/icons/green-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<img
											v-else
											src="@/assets/images/icons/gray-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<div class="font-semibold text-sm">
											Hình ảnh
										</div>
									</div>
									<div class="flex items-center gap-2">
										<img
											v-if="
												data.data?.other_info
													?.description
											"
											src="@/assets/images/icons/green-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<img
											v-else
											src="@/assets/images/icons/gray-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<div class="font-semibold text-sm">
											Mô tả
										</div>
									</div>
									<div class="flex items-center gap-2">
										<img
											v-if="
												data.data
													?.is_active_operating_license
											"
											src="@/assets/images/icons/green-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<img
											v-else
											src="@/assets/images/icons/gray-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<div class="font-semibold text-sm">
											Giấy phép hoạt động còn hiệu lực
										</div>
									</div>
									<div class="flex items-center gap-2">
										<img
											v-if="
												data.data
													?.is_management_certifications_verified
											"
											src="@/assets/images/icons/green-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<img
											v-else
											src="@/assets/images/icons/gray-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<div class="font-semibold text-sm">
											Bằng cấp, chứng chỉ của cấp Quản lý
										</div>
									</div>
									<div class="flex items-center gap-2">
										<img
											v-if="data.data?.is_have_parent_app"
											src="@/assets/images/icons/green-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<img
											v-else
											src="@/assets/images/icons/gray-tick.png"
											alt=""
											class="w-4 h-4"
										/>
										<div class="font-semibold text-sm">
											Có app dành cho Phụ huynh (chuyển
											đổi số)
										</div>
									</div>
								</div>
							</div>
							<div
								class="flex items-center gap-1 shadow-sm cursor-pointer py-2 px-3 xl-down:mt-2 h-8 md-down:mt-4 md-down:border md-down:border-neutral-200 md-down:rounded"
								@click="sideOverShow = true"
							>
								<img
									class="w-4 h-4"
									src="@/assets/images/icons/pen_fill.png"
									alt=""
								/>
								<div
									class="text-neutral-600 font-semibold text-xs whitespace-nowrap"
								>
									Cập nhật thông tin
								</div>
							</div>
							<div class="bottom-checklist gap-3">
								<div class="flex items-center gap-2">
									<img
										v-if="data.data?.logo_url"
										src="@/assets/images/icons/green-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<img
										v-else
										src="@/assets/images/icons/gray-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<div class="font-semibold text-sm">
										Logo
									</div>
								</div>
								<div class="flex items-center gap-2">
									<img
										v-if="data.data?.background_url"
										src="@/assets/images/icons/green-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<img
										v-else
										src="@/assets/images/icons/gray-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<div class="font-semibold text-sm">
										Hình ảnh
									</div>
								</div>
								<div class="flex items-center gap-2">
									<img
										v-if="
											data.data?.other_info?.description
										"
										src="@/assets/images/icons/green-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<img
										v-else
										src="@/assets/images/icons/gray-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<div class="font-semibold text-sm">
										Mô tả
									</div>
								</div>
								<div class="flex items-center gap-2">
									<img
										v-if="
											data.data
												?.is_active_operating_license
										"
										src="@/assets/images/icons/green-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<img
										v-else
										src="@/assets/images/icons/gray-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<div class="font-semibold text-sm">
										Giấy phép hoạt động còn hiệu lực
									</div>
								</div>
								<div class="flex items-center gap-2">
									<img
										v-if="
											data.data
												?.is_management_certifications_verified
										"
										src="@/assets/images/icons/green-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<img
										v-else
										src="@/assets/images/icons/gray-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<div class="font-semibold text-sm">
										Bằng cấp, chứng chỉ của cấp Quản lý
									</div>
								</div>
								<div class="flex items-center gap-2">
									<img
										v-if="data.data?.is_have_parent_app"
										src="@/assets/images/icons/green-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<img
										v-else
										src="@/assets/images/icons/gray-tick.png"
										alt=""
										class="w-4 h-4"
									/>
									<div class="font-semibold text-sm">
										Có app dành cho Phụ huynh (chuyển đổi
										số)
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="mb-10" v-if="alertShow">
						<div
							class="bg-yellow-50 border-l-4 border-yellow-600 p-4 rounded-md"
						>
							<div class="flex justify-between gap-4">
								<div class="flex items-center gap-4">
									<div>
										<img
											src="@/assets/images/logo/logo.svg"
											alt="luca-logo"
											height="24"
											width="35"
										/>
									</div>
									<div class="pt-1 text-neutral-600 text-sm">
										Trung tâm hoàn thiện thông tin tại
										<a
											:href="data.data.shor_form_url"
											class="underline text-green-500 font-medium cursor-pointer"
											target="_blank"
											rel="noopener"
										>
											form hồ sơ nâng cao</a
										>
										để Luca Hub xác minh điều kiện được tick
										xanh và hiển thị trung tâm nổi bật hơn
										trên hệ thống.
									</div>
								</div>
								<div>
									<svg
										width="12"
										height="12"
										viewBox="0 0 12 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
										class="cursor-pointer"
										@click="alertShow = false"
									>
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L6 4.58579L10.2929 0.292893C10.6834 -0.0976311 11.3166 -0.0976311 11.7071 0.292893C12.0976 0.683417 12.0976 1.31658 11.7071 1.70711L7.41421 6L11.7071 10.2929C12.0976 10.6834 12.0976 11.3166 11.7071 11.7071C11.3166 12.0976 10.6834 12.0976 10.2929 11.7071L6 7.41421L1.70711 11.7071C1.31658 12.0976 0.683417 12.0976 0.292893 11.7071C-0.0976311 11.3166 -0.0976311 10.6834 0.292893 10.2929L4.58579 6L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z"
											fill="#E5A42B"
										/>
									</svg>
								</div>
							</div>
						</div>
					</div>
					<div class="relative mb-10">
						<div
							class="absolute inset-0 flex items-center"
							aria-hidden="true"
						>
							<div class="w-full border-t border-neutral-300" />
						</div>
						<div class="relative flex justify-center">
							<div
								class="bg-body px-2 text-neutral-600 font-semibold whitespace-nowrap flex flex-nowrap items-center text-2xl md-down:text-lg justify-center gap-6"
							>
								<div
									class="cursor-pointer"
									@click="prevDay"
									v-if="date.month > 1 || date.year > 2025"
								>
									<svg
										width="8"
										height="12"
										viewBox="0 0 8 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M6.5 1L1.5 6L6.5 11"
											stroke="#48566A"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</div>
								<div
									v-if="date"
									class="flex items-center gap-1"
								>
									<img
										class="w-4 h-4"
										src="@/assets/images/icons/calendar.png"
										alt="calendar"
									/>
									<div>Tháng {{ dateTitle }}</div>
								</div>
								<div
									class="cursor-pointer"
									@click="nextDay"
									v-if="
										date.month < new Date().getMonth() ||
										date.year < new Date().getFullYear()
									"
								>
									<svg
										width="8"
										height="12"
										viewBox="0 0 8 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M1.5 11L6.5 6L1.5 1"
											stroke="#48566A"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</div>
							</div>
						</div>
					</div>
					<div class="flex items-center gap-2 mb-10 md-down:flex-col">
						<div class="shadow-sm rounded-lg bg-white p-4 w-1/2 md-down:w-full">
							<div class="flex items-center gap-2 mb-6">
								<img
									src="@/assets/images/icons/eye-purple.png"
									alt=""
								/>
								<div class="text-neutral-500">
									Số lượt xem hồ sơ trung tâm
								</div>
							</div>
							<div class="text-neutral-600 font-bold text-4xl">
								{{ data?.lucahub_stats?.view_count }}
							</div>
						</div>
						<div class="shadow-sm rounded-lg bg-white p-4 w-1/2 md-down:w-full">
							<div class="flex items-center gap-2 mb-6">
								<img
									src="@/assets/images/icons/telephone-fill.png"
									alt=""
								/>
								<div class="text-neutral-500">
									Số lượt liên hệ qua nút "Liên hệ ngay"
								</div>
							</div>
							<div class="text-neutral-600 font-bold text-4xl">
								{{
									data?.lucahub_stats
										?.parent_contact_information_count
								}}
							</div>
						</div>
					</div>
					<div
						class="mb-10"
						v-if="
							parentContactInformation &&
							parentContactInformation.length > 0
						"
					>
						<div
							class="text-neutral-600 text-lg font-semibold mb-4"
						>
							Phụ huynh đã liên hệ
						</div>
						<div class="shadow-sm sm:rounded-2xl overflow-hidden">
							<div
								class="__record__table overflow-x-auto scroll-bar"
							>
								<div
									class="overflow-hidden sm:rounded-t-2xl w-fit min-w-full"
								>
									<table
										class="min-w-full divide-y text-neutral-200"
									>
										<thead class="bg-neutral-100 h-16">
											<tr>
												<th
													scope="col"
													class="px-6 py-6 text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap text-left"
												>
													Ngày liên hệ
												</th>
												<th
													scope="col"
													class="px-6 py-6 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													Tên phụ huynh
												</th>
												<th
													scope="col"
													class="px-6 py-6 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													EMAIL, SĐT
												</th>
												<th
													scope="col"
													class="px-6 py-6 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													Câu hỏi
												</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="(
													item, index
												) in data.parent_contact_informations"
												:key="item.id"
												:class="
													index % 2 === 0
														? 'bg-white'
														: 'bg-neutral-50'
												"
												class="text-neutral-700"
											>
												<td
													class="px-6 py-6 whitespace-nowrap text-neutral-700"
													style="width: 130px"
												>
													{{
														dayjs(
															item?.inserted_at
														).format('DD/MM/YYYY')
													}}
												</td>
												<td
													class="px-6 py-6 whitespace-nowrap text-neutral-700"
												>
													{{ item?.parent_name }}
												</td>
												<td
													class="px-6 py-6 whitespace-nowrap text-neutral-700"
												>
													<div
														class="flex items-center gap-1"
													>
														<img
															src="@/assets/images/icons/telephone.png"
															alt=""
														/>
														<div>
															{{
																item?.parent_phone
															}}
														</div>
													</div>
													<div
														class="flex items-center gap-1"
													>
														<img
															src="@/assets/images/icons/envelope.png"
															alt=""
														/>
														<div>
															{{
																item?.parent_email
															}}
														</div>
													</div>
												</td>
												<td
													class="px-6 py-6 whitespace-nowrap text-neutral-700"
													style="min-width: 400px"
												>
													<div
														v-for="q in item.parent_question"
														:key="q"
														class="flex flex-col"
													>
														<div>{{ q }}</div>
													</div>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
					<div v-if="orgList && orgList.length > 0">
						<div
							class="text-neutral-600 text-lg font-semibold mb-4"
						>
							Danh sách cơ sở trực thuộc trung tâm
						</div>
						<div class="shadow-sm sm:rounded-2xl overflow-hidden">
							<div
								class="__record__table overflow-x-auto scroll-bar"
							>
								<div
									class="overflow-hidden sm:rounded-t-2xl w-fit min-w-full"
								>
									<table
										class="min-w-full divide-y text-neutral-200"
									>
										<thead class="bg-neutral-100 h-16">
											<tr>
												<th
													scope="col"
													class="px-6 py-6 text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap text-left"
												>
													CƠ SỞ GIÁO DỤC
												</th>
												<th
													scope="col"
													class="px-6 py-6 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
												>
													Xem hồ sơ trên Luca Hub
												</th>
											</tr>
										</thead>
										<tbody>
											<tr
												v-for="(
													item, index
												) in data.org_list"
												:key="item.id"
												:class="
													index % 2 === 0
														? 'bg-white'
														: 'bg-neutral-50'
												"
												class="text-neutral-700"
											>
												<td
													class="px-6 py-6 whitespace-nowrap text-neutral-700"
												>
													<div>
														{{ item?.name }}
													</div>
												</td>
												<td
													class="px-6 py-6 whitespace-nowrap text-neutral-700"
												>
													<div class="luca-button">
														Xem hồ sơ
													</div>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div
					v-else-if="data.is_pending_accepted_profile"
					class="flex items-center justify-center mt-20"
				>
					<div
						class="flex items-center justify-center flex-col max-w-3xl"
					>
						<img
							style="height: 120px"
							src="@/assets/images/illustrations/shield.png"
							alt="luca-hub"
						/>
						<div
							class="text-neutral-700 text-3xl font-semibold my-8 text-center"
							v-if="data.is_have_lucahub_profile"
						>
							Đang chờ phê duyệt
						</div>
						<div class="text-neutral-400 text-lg mb-4 text-center">
							Cảm ơn bạn đã đăng ký tham gia Luca Hub!<br />
							Chúng tôi đang kiểm tra thông tin của Trung tâm để
							đảm bảo độ chính xác và đầy đủ. <br />Bạn sẽ nhận
							được thông báo ngay khi Trung tâm được duyệt và hiển
							thị trên nền tảng.
						</div>
						<div
							class="flex items-center gap-6 md-down:flex-col md-down:w-full"
							v-if="!data.is_have_lucahub_profile"
						>
							<a
								target="_blank"
								href="https://www.luca.education/lucahub-partners"
								class="luca-button md-down:w-full md-down:text-center"
							>
								Tìm hiểu thêm
							</a>
							<button
								@click="registerLucaHub"
								type="button"
								style="height: 46px"
								class="button-p text-center w-max md-down:w-full"
							>
								Đăng ký ngay
							</button>
						</div>
					</div>
				</div>
				<div v-else class="flex items-center justify-center mt-20">
					<div
						class="flex items-center justify-center flex-col max-w-2xl"
					>
						<img
							style="height: 45px"
							src="@/assets/images/illustrations/puzzle.png"
							alt="luca-hub"
						/>
						<div class="text-neutral-400 text-lg my-8 text-center">
							Tham gia Luca Hub để kết nối miễn phí với hàng ngàn
							phụ huynh đang tìm kiếm dịch vụ giáo dục đặc biệt
						</div>
						<div class="flex items-center gap-6 md-down:flex-col md-down:w-full">
							<a
								target="_blank"
								href="https://www.luca.education/lucahub-partners"
								class="luca-button md-down:w-full md-down:text-center"
							>
								Tìm hiểu thêm
							</a>
							<button
								@click="registerLucaHub"
								type="button"
								style="height: 46px"
								class="button-p text-center w-max md-down:w-full"
							>
								Đăng ký ngay
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<style scoped>
.container {
	position: relative;
	width: 240px;
	height: 240px;
}

.progress-circle {
	width: 100%;
	height: 100%;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.inner-circle {
	position: absolute;
	width: 200px;
	height: 200px;
	border-radius: 50%;
	background-color: white;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 36px;
	font-weight: bold;
	color: #3c4858;
}
.cover-img {
	height: 450px;
}
.logo {
	width: 100px;
	height: 100px;
	bottom: -70px;
	left: 16px;
	border-radius: 50%;
	z-index: 2;
	box-shadow: 0px 3px 8px -1px #3232470d;
}
.logo-media {
	position: absolute;
	inset: 0;
	border-radius: 50%;
	overflow: hidden;
}
.logo-media img {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}
.change-bg {
	position: absolute;
	bottom: 26px;
	right: 16px;
	z-index: 2;
}
.change-logo {
	position: absolute;
	bottom: 0;
	right: 0;
	background: #f1f5f9;
	border: 2px solid #ffffff;
	border-radius: 50%;
	z-index: 3;
}
.profile-info {
	margin-left: 120px;
}
.profile-progress {
	width: 364px;
}
.block-1 {
	width: calc(100% - 364px);
}
.highlight-icon {
	width: 48px;
	height: 48px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
.highlight-icon img {
	max-width: 100%;
	max-height: 100%;
	width: auto;
	height: auto;
	object-fit: contain;
}
.highlight-text {
	min-width: 0;
	flex: 1;
}
.highlight-item {
	white-space: nowrap;
}
.side-checklist {
	display: none;
	flex-direction: column;
	gap: 12px;
}
.bottom-checklist {
	display: flex;
	flex-direction: column;
	gap: 12px;
}
@media screen and (max-width: 1279px) {
	.profile-progress {
		width: 100% !important;
	}
	.container {
		width: 150px;
		height: 150px;
	}
	.inner-circle {
		width: 120px;
		height: 120px;
		font-size: 24px;
	}
	.block-1 {
		width: 100%;
	}
	.side-checklist {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
	.bottom-checklist {
		display: none;
	}
}
@media screen and (max-width: 767px) {
	.cover-img {
		height: 180px;
	}
	.change-bg {
		top: 12px;
		right: 12px;
		bottom: auto;
		flex-wrap: wrap;
	}
	.change-bg > div {
		flex-shrink: 1;
		min-width: 0;
	}
	.logo {
		left: 50%;
		transform: translateX(-50%);
		bottom: -48px;
		width: 96px;
		height: 96px;
	}
	.profile-info {
		margin-left: 0;
		padding-top: 56px;
	}
	.container {
		width: 180px;
		height: 180px;
	}
	.inner-circle {
		width: 148px;
		height: 148px;
		font-size: 32px;
	}
	.side-checklist {
		display: none;
	}
	.bottom-checklist {
		display: flex;
		flex-direction: column;
		width: 100%;
		margin-top: 16px;
		gap: 12px;
	}
	.profile-progress {
		gap: 0;
	}
	.highlight-item {
		white-space: normal;
		width: 100%;
	}
	.highlight-icon {
		width: 48px;
		height: 48px;
	}
}
</style>
